// SCR-STAFF-952 — قواعد اطلاع‌رسانی (US-MSG-030 تا 033؛ AP-MSG-01): فهرست قواعد (پیش‌فرض و سفارشی) با رویداد، گیرنده، قالب، زمان‌بندی،
// هدف و تعداد پیام زمان‌بندی‌شده؛ فعال/غیرفعال (غیرفعال‌سازی پیام‌های آینده را لغو می‌کند — AC-MSG-030.2)؛ ایجاد و ویرایش (هر ویرایش نسخه
// تازه — BR-MSG-030). رویدادهای ماژول‌های ساخته‌نشده «به‌زودی» نشان داده می‌شوند. پیش از Go-live پیام مراجعه‌کننده ارسال نمی‌شود (BR-MSG-032).
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import {
  unwrap,
  type MessagePurpose,
  type NotificationRuleView,
  type RuleRecipient,
  type RuleTimingKind,
  type SaveRuleRequest,
} from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, FormDialog, ListPage, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { WriteNotice } from '../settings/common';
import { msgKeys, SCR_STAFF_952, usePurposeLabel, useTemplateLabel } from './common';

const RECIPIENTS: readonly RuleRecipient[] = ['Patient', 'Provider', 'StaffRole'];
const TIMINGS: readonly RuleTimingKind[] = ['Immediate', 'BeforeAppointment', 'AfterEvent'];
const PURPOSES: readonly MessagePurpose[] = ['Transactional', 'Internal'];
const ROLES = ['Receptionist', 'Manager', 'TenantOwner', 'TenantAdmin', 'Nurse', 'Physician'] as const;

interface RuleDraft {
  id?: string;
  name: string;
  trigger: string;
  recipient: RuleRecipient;
  recipientRole: string;
  templateCode: string;
  timing: RuleTimingKind;
  offsetMinutes: string;
  purpose: MessagePurpose;
  requiresReply: boolean;
}

function draftOf(rule: NotificationRuleView | null, trigger: string): RuleDraft {
  return rule
    ? {
        id: rule.id,
        name: rule.name,
        trigger: rule.trigger,
        recipient: rule.recipient,
        recipientRole: rule.recipientRole ?? '',
        templateCode: rule.templateCode,
        timing: rule.timing,
        offsetMinutes: String(rule.offsetMinutes),
        purpose: rule.purpose,
        requiresReply: rule.requiresReply,
      }
    : {
        name: '',
        trigger,
        recipient: 'Patient',
        recipientRole: '',
        templateCode: 'msg.sch.reminder_24h',
        timing: 'BeforeAppointment',
        offsetMinutes: '1440',
        purpose: 'Transactional',
        requiresReply: false,
      };
}

export function RulesPage() {
  const { t } = useTranslation('msg');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('msg.rule.manage');
  const queryClient = useQueryClient();
  const purposeLabel = usePurposeLabel();
  const templateLabel = useTemplateLabel();
  const [draft, setDraft] = useState<RuleDraft | null>(null);
  const rules = useQuery({
    queryKey: msgKeys.rules(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/messaging/rules')),
  });
  const templates = useQuery({
    queryKey: msgKeys.templates(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/messaging/templates')),
    enabled: draft !== null,
  });
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: msgKeys.rules(session?.tenantId) });

  const toggle = useMutation({
    mutationFn: (rule: NotificationRuleView) =>
      rule.isActive
        ? unwrap(
            tenantApi.POST('/api/v1/messaging/rules/{id}:deactivate', { params: { path: { id: rule.id } } }),
          )
        : unwrap(
            tenantApi.POST('/api/v1/messaging/rules/{id}:activate', { params: { path: { id: rule.id } } }),
          ),
    onSuccess: invalidate,
  });
  const save = useMutation({
    mutationFn: (value: RuleDraft) => {
      const body: SaveRuleRequest = {
        code: null,
        name: value.name.trim(),
        trigger: value.trigger,
        branchIds: null,
        bookingChannels: null,
        serviceCategoryIds: null,
        recipient: value.recipient,
        recipientRole: value.recipient === 'StaffRole' ? value.recipientRole || null : null,
        templateCode: value.templateCode,
        timing: value.timing,
        offsetMinutes: value.timing === 'Immediate' ? 0 : Number(value.offsetMinutes || '0'),
        purpose: value.purpose,
        requiresReply: value.requiresReply,
        isActive: true,
      };
      return value.id
        ? unwrap(tenantApi.PUT('/api/v1/messaging/rules/{id}', { params: { path: { id: value.id } }, body }))
        : unwrap(tenantApi.POST('/api/v1/messaging/rules', { body }));
    },
    onSuccess: () => {
      setDraft(null);
      invalidate();
    },
  });

  const triggers = rules.data?.triggers ?? [];
  const triggerLabel = (trigger: string) =>
    t(`msg:triggers.${trigger.replaceAll('.', '_')}`, { defaultValue: trigger });

  const columns: DataTableColumn<NotificationRuleView>[] = [
    {
      id: 'name',
      headerKey: 'msg:rules.columns.name',
      cell: (row) => (
        <Stack spacing={0.25}>
          <span>{row.name}</span>
          {row.isDefault ? (
            <Chip size="small" label={t('msg:rules.default')} sx={{ alignSelf: 'flex-start' }} />
          ) : null}
        </Stack>
      ),
    },
    {
      id: 'trigger',
      headerKey: 'msg:rules.columns.trigger',
      cell: (row) => (
        <Stack spacing={0.25}>
          <span>{triggerLabel(row.trigger)}</span>
          {!row.triggerActive ? (
            <Chip
              size="small"
              color="default"
              label={t('msg:rules.trigger_inactive')}
              sx={{ alignSelf: 'flex-start' }}
            />
          ) : null}
        </Stack>
      ),
    },
    {
      id: 'recipient',
      headerKey: 'msg:rules.columns.recipient',
      cell: (row) =>
        t(`msg:rules.recipient.${row.recipient}`) + (row.recipientRole ? ` (${row.recipientRole})` : ''),
    },
    {
      id: 'template',
      headerKey: 'msg:rules.columns.template',
      cell: (row) => templateLabel(row.templateCode),
    },
    {
      id: 'timing',
      headerKey: 'msg:rules.columns.timing',
      cell: (row) =>
        row.timing === 'Immediate'
          ? t('msg:rules.timing.Immediate')
          : t(`msg:rules.timing_offset.${row.timing}`, { minutes: faDigits(String(row.offsetMinutes)) }),
    },
    { id: 'purpose', headerKey: 'msg:rules.columns.purpose', cell: (row) => purposeLabel(row.purpose) },
    {
      id: 'pending',
      headerKey: 'msg:rules.columns.pending',
      cell: (row) => (
        <span data-testid={`rule-pending-${row.code}`}>{faDigits(String(row.pendingCount))}</span>
      ),
    },
    {
      id: 'active',
      headerKey: 'msg:rules.columns.active',
      cell: (row) => (
        <Switch
          checked={row.isActive}
          onChange={() => toggle.mutate(row)}
          disabled={!write.canWrite || toggle.isPending}
          slotProps={{
            input: {
              'aria-label': t('msg:rules.toggle', { name: row.name }),
              'data-testid': `rule-toggle-${row.code}`,
            } as never,
          }}
        />
      ),
    },
    {
      id: 'actions',
      headerKey: 'msg:rules.columns.actions',
      cell: (row) => (
        <Tooltip title={t('msg:rules.edit')}>
          <span>
            <IconButton
              aria-label={t('msg:rules.edit')}
              onClick={() => setDraft(draftOf(row, row.trigger))}
              disabled={!write.canWrite}
              data-testid={`rule-edit-${row.code}`}
            >
              <EditOutlinedIcon />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  const patientTemplates = (templates.data?.items ?? []).filter((item) => item.audience === 'Patient');

  return (
    <ListPage
      screenId={SCR_STAFF_952}
      titleKey="msg:rules.title"
      subtitleKey="msg:rules.subtitle"
      actions={
        <Button
          variant="contained"
          startIcon={<AddOutlinedIcon />}
          onClick={() => setDraft(draftOf(null, triggers[0]?.trigger ?? ''))}
          disabled={!write.canWrite}
          data-testid="rule-create"
        >
          {t('msg:rules.create')}
        </Button>
      }
      filters={
        <Stack spacing={1}>
          <WriteNotice write={write} />
          {rules.data && !rules.data.live ? (
            <Alert severity="info" data-testid="rules-not-live">
              {t('msg:rules.not_live')}
            </Alert>
          ) : null}
          {toggle.error ? <ErrorState error={toggle.error} compact /> : null}
        </Stack>
      }
    >
      <DataTable
        captionKey="msg:rules.caption"
        columns={columns}
        rows={rules.data?.items}
        getRowId={(row) => row.id}
        isLoading={rules.isPending}
        error={rules.error}
        onRetry={() => void rules.refetch()}
        emptyTitleKey="msg:rules.empty"
      />
      {draft ? (
        <FormDialog
          open
          titleKey={draft.id ? 'msg:rules.edit_title' : 'msg:rules.create_title'}
          screenId={SCR_STAFF_952}
          onSubmit={() => save.mutate(draft)}
          onClose={() => {
            setDraft(null);
            save.reset();
          }}
          isDirty
          isSubmitting={save.isPending}
          submitError={save.error}
        >
          <TextField
            label={t('msg:rules.fields.name')}
            value={draft.name}
            onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            required
            slotProps={{ htmlInput: { 'data-testid': 'rule-name' } }}
          />
          <TextField
            select
            label={t('msg:rules.fields.trigger')}
            value={draft.trigger}
            onChange={(event) => setDraft({ ...draft, trigger: event.target.value })}
            slotProps={{ htmlInput: { 'data-testid': 'rule-trigger' } }}
          >
            {triggers.map((item) => (
              <MenuItem key={item.trigger} value={item.trigger}>
                {triggerLabel(item.trigger)}
                {item.active ? '' : ` — ${t('msg:rules.trigger_inactive')}`}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('msg:rules.fields.recipient')}
            value={draft.recipient}
            onChange={(event) => setDraft({ ...draft, recipient: event.target.value as RuleRecipient })}
          >
            {RECIPIENTS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`msg:rules.recipient.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          {draft.recipient === 'StaffRole' ? (
            <TextField
              select
              label={t('msg:rules.fields.role')}
              value={draft.recipientRole}
              onChange={(event) => setDraft({ ...draft, recipientRole: event.target.value })}
            >
              {ROLES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`iam:roles.system.${value}`, { defaultValue: value })}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          {draft.recipient === 'Patient' ? (
            <TextField
              select
              label={t('msg:rules.fields.template')}
              value={draft.templateCode}
              onChange={(event) => setDraft({ ...draft, templateCode: event.target.value })}
              slotProps={{ htmlInput: { 'data-testid': 'rule-template' } }}
            >
              {(patientTemplates.length > 0
                ? patientTemplates.map((item) => item.code)
                : [draft.templateCode]
              ).map((code) => (
                <MenuItem key={code} value={code}>
                  {templateLabel(code)}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              label={t('msg:rules.fields.notification_type')}
              value={draft.templateCode}
              onChange={(event) => setDraft({ ...draft, templateCode: event.target.value })}
              slotProps={{ htmlInput: { dir: 'ltr' } }}
            />
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label={t('msg:rules.fields.timing')}
              value={draft.timing}
              onChange={(event) => setDraft({ ...draft, timing: event.target.value as RuleTimingKind })}
              sx={{ flex: 1 }}
            >
              {TIMINGS.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`msg:rules.timing.${value}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="number"
              label={t('msg:rules.fields.offset')}
              value={draft.offsetMinutes}
              disabled={draft.timing === 'Immediate'}
              onChange={(event) => setDraft({ ...draft, offsetMinutes: event.target.value })}
              sx={{ flex: 1 }}
              slotProps={{ htmlInput: { min: 0, 'data-testid': 'rule-offset' } }}
            />
          </Stack>
          <TextField
            select
            label={t('msg:rules.fields.purpose')}
            value={draft.purpose}
            onChange={(event) => setDraft({ ...draft, purpose: event.target.value as MessagePurpose })}
          >
            {PURPOSES.map((value) => (
              <MenuItem key={value} value={value}>
                {purposeLabel(value)}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={draft.requiresReply}
                onChange={(event) => setDraft({ ...draft, requiresReply: event.target.checked })}
              />
            }
            label={t('msg:rules.fields.requires_reply')}
          />
        </FormDialog>
      ) : null}
    </ListPage>
  );
}
